import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { Coupon } from 'src/app/shared/models/dtos/responses/coupon/coupon';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { Subscription } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { toPng } from 'html-to-image';
import { download } from 'src/app/utils/file';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TopHeaderService } from 'src/app/core/services/top-header/top-header.service';
import { RewardType } from 'src/app/shared/models/constants/reward-type';

@Component({
  selector: 'app-product-coupon-content',
  templateUrl: './product-coupon-content.component.html',
  styleUrls: ['./product-coupon-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Organisms that shows the main content of Product Coupon page
 */ // eslint-disable-next-line
export class ProductCouponContentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() title!: string;
  @Input() label1!: string;
  @Input() label2!: string;
  @Input() label3!: string;
  @Input() coupon!: Coupon;
  hasDisclaimer = false;
  translatedStrings = {
    'download-coupon': 'download-coupon',
    'copy-coupon-card-code': 'copy-coupon-card-code',
    print: 'print',
    codeCopied: 'code-copied',
    downloadSuccess: 'successfully-downloaded',
    downloadFail: 'error-downloading-coupon',
    printFail: 'error-printing-coupon',
    disclaimer: 'disclaimer-coupon',
    printSuccess: 'successfully-printed',
  };

  @ViewChild('buttons') buttonsArea?: ElementRef<HTMLDivElement>;
  @ViewChild('tooltip') tooltip?: MatTooltip;
  @ViewChild('couponCard') couponCardComponent?: ElementRef<HTMLElement>;

  private subsink = new Subscription();

  /**
   * @constructor
   */
  constructor(
    private clipboard: Clipboard,
    private translationService: TranslationService,
    private readonly topHeaderService: TopHeaderService,
    private readonly snackBarService: SnackBarService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.translatedStrings);

    if (this.coupon.rewardType === RewardType.CUSTOMER_SERVICE_EXTENSION) {
      this.hasDisclaimer = true;
    }
  }

  /**
   * businessName
   */
  get businessName() {
    return this.topHeaderService.business?.businessName || '';
  }

  /**
   * after view init
   */
  ngAfterViewInit() {
    let maxWidth = 0;
    const childNodes = this.buttonsArea?.nativeElement
      .childNodes as NodeListOf<HTMLDivElement>;
    if (childNodes) {
      childNodes.forEach((node: HTMLDivElement) => {
        const nodeWidth = node.clientWidth;
        if (nodeWidth > maxWidth) {
          maxWidth = nodeWidth;
        }
      });

      childNodes.forEach((node: HTMLDivElement) => {
        node.style.width = `${maxWidth}px`;
      });
    }
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * [TODO] Copy text to clipboard
   */
  copy() {
    this.clipboard.copy(this.coupon.code as string);
    this.snackBarService.success(this.translatedStrings.codeCopied);
  }

  /**
   * Save a coupon as png
   */
  downloadCoupon() {
    if (this.couponCardComponent?.nativeElement) {
      const couponElement = this.couponCardComponent.nativeElement;
      couponElement.style.backgroundColor = 'white';
      toPng(couponElement)
        .then((couponCardPng) => {
          download(`coupon-${this.coupon.code}.png`, couponCardPng);
          this.snackBarService.success(this.translatedStrings.downloadSuccess);
        })
        .catch(() => {
          this.snackBarService.error(this.translatedStrings.downloadFail);
        });
    }
  }

  /**
   * Saves a coupon as pdf
   */
  printCoupon() {
    if (this.couponCardComponent?.nativeElement) {
      toPng(this.couponCardComponent?.nativeElement)
        .then((couponCardPng) => {
          const w = window.open('', '_new');
          if (w) {
            const img = w.document.createElement('img');
            img.src = couponCardPng;
            img.onload = () => {
              w.document.body.appendChild(img);
              w.onafterprint = () => {
                w.close();
              };
              w.print();
            };
          }
          this.snackBarService.success(this.translatedStrings.printSuccess);
        })
        .catch(() => {
          this.snackBarService.error(this.translatedStrings.printFail);
        });
    }
  }
}
